import { useCallback, useRef, useState } from "react"
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerClose } from "../components/ui/drawer"
import type { CogoElement } from "../core/types"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"

type CanonicalDrawerElement = CogoElement & {
  activeSnapPoint?: number | string | null
  snapPoint?: number | string | null
  snapPoints?: Array<number | string>
}

function resolveBoolean(
  value: boolean | string | undefined,
  resolveSymbol: (value: string) => unknown,
  fallback: boolean,
): boolean {
  if (value === undefined) return fallback
  if (typeof value === "boolean") return value
  const resolved = resolveSymbol(value)
  if (typeof resolved === "boolean") return resolved
  return String(resolved ?? value).trim().toLowerCase() === "true"
}

function resolveSnapPoint(
  value: number | string | null | undefined,
  resolveSymbol: (value: string) => unknown,
): number | string | null | undefined {
  if (typeof value !== "string") return value
  const resolved = resolveSymbol(value)
  return typeof resolved === "number" || typeof resolved === "string" || resolved === null
    ? resolved
    : value
}

export function DrawerRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  const drawerElement = element as CanonicalDrawerElement
  const openSource = element.open ?? element.visible
  const requestedOpen = element.variant === "open"
    || resolveBoolean(openSource, resolveSymbol, false)
  const [isOpen, setIsOpen] = useState(requestedOpen)
  const snapPoints = drawerElement.snapPoints
  const requestedSnapPoint = resolveSnapPoint(
    drawerElement.activeSnapPoint ?? drawerElement.snapPoint ?? snapPoints?.[0],
    resolveSymbol,
  )
  const [activeSnapPoint, setActiveSnapPoint] = useState(requestedSnapPoint)
  const previousElementRef = useRef(element)

  if (previousElementRef.current !== element) {
    previousElementRef.current = element
    if (isOpen !== requestedOpen) setIsOpen(requestedOpen)
    if (!Object.is(activeSnapPoint, requestedSnapPoint)) {
      setActiveSnapPoint(requestedSnapPoint)
    }
  }
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  // JITRULE Task 2: the heading peek reads `.text` directly in its own find
  // predicate — lift each child before that read.
  const liftedChildren = element.children?.map(liftV4Props)
  const semanticTitleChild = liftedChildren?.find((child) => child.type === "heading" && child.text)
  const semanticTitleSource = semanticTitleChild?.text
  const semanticTitle = semanticTitleSource ? String(resolveSymbol(semanticTitleSource) ?? "") : "Drawer"

  const canonicalSides = ["top", "bottom", "left", "right"] as const
  const side = canonicalSides.includes(element.side as (typeof canonicalSides)[number])
    ? (element.side as (typeof canonicalSides)[number])
    : element.variant === "right" ? "right" : "left"
  const isVerticalEdge = side === "top" || side === "bottom"

  const handleClose = useCallback(() => {
    setIsOpen(false)
    dispatchEvent(element.id, "close", {})
  }, [element.id, dispatchEvent])

  const handleSnapPointChange = useCallback((snapPoint: number | string | null) => {
    setActiveSnapPoint(snapPoint)
    dispatchEvent(element.id, "snapPointChange", { snapPoint })
  }, [dispatchEvent, element.id])

  const drawer = (
    <Drawer
      open={isOpen}
      onOpenChange={(open) => {
        setIsOpen(open)
        if (!open) handleClose()
        else dispatchEvent(element.id, "open", {})
      }}
      direction={side}
      snapPoints={snapPoints}
      activeSnapPoint={activeSnapPoint}
      setActiveSnapPoint={handleSnapPointChange}
      data-testid={`drawer-${element.id}`}
    >
      <DrawerContent
        aria-describedby={undefined}
        className={cn(
          "flex flex-col",
          isVerticalEdge ? "max-h-[85vh] w-full" : "h-full w-72",
          side === "right" ? "ml-auto" : side === "left" ? "mr-auto" : undefined,
          element.className,
        )}
      >
        {/* Header */}
        <DrawerHeader>
          <DrawerTitle className={!text && !semanticTitleSource ? "sr-only" : undefined}>
            {text ?? semanticTitle}
          </DrawerTitle>
          <DrawerClose
            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </DrawerClose>
        </DrawerHeader>
        {/* Navigation items / children */}
        <nav className="flex-1 overflow-y-auto">
          {element.children
            ?.filter((child) => child.id !== semanticTitleChild?.id || Boolean(text))
            .map((child) => (
              <ElementRenderer key={child.id} element={child} />
            ))}
        </nav>
      </DrawerContent>
    </Drawer>
  )

  return (
    <span
      data-atom-type="drawer"
      data-side={side}
      data-is-open={isOpen ? "true" : "false"}
      data-snap-point={activeSnapPoint ?? undefined}
      data-testid={`drawer-${element.id}`}
      aria-expanded={isOpen}
      className={element.className ? cn("inline-block", element.className) : undefined}
    >
      {drawer}
    </span>
  )
}
